/**
 * Display Settings: What the Conversation Shows (feature guide)
 *
 * Screenshots were taken from the Sokuji v0.41.1 desktop app.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "Display Settings: What the Conversation Shows",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "The toolbar above the conversation controls what you see: the original, the translation or both for each side, the text size, a compact view and the colours."
  },
  "steps": [
    {
      "title": "The toolbar",
      "content": "From left to right: which lines to show for <strong>Me</strong> and for <strong>Other</strong>, smaller and larger text, compact view, export, display settings and clear conversation.",
      "screenshot": "01-toolbar.png"
    },
    {
      "title": "Who is speaking",
      "content": "Your lines are marked <strong>Me</strong> and the other side’s <strong>Other</strong>. With Soniox (your own key, or KizunaAI’s Soniox engine), once two or more people are heard on the other side, each one gets a numbered avatar and a name, <strong>Speaker 1</strong>, <strong>Speaker 2</strong> and so on. In a face-to-face conversation, each translation also shows <strong>L</strong> or <strong>R</strong> for the ear it played in, or a muted speaker when it was read to no one."
    },
    {
      "title": "Original, translation or both",
      "content": "Click the Me or Other button to cycle through <strong>Src</strong> (only the original), <strong>Trans</strong> (only the translation), <strong>Both</strong> and <strong>Off</strong> (hide that side). Here, Me shows only the translation.",
      "screenshot": "02-me-translation-only.png"
    },
    {
      "title": "Text size",
      "content": "Use the smaller and larger text buttons to change the size, from 12 to 64 pixels.",
      "screenshot": "03-larger-text.png"
    },
    {
      "title": "Compact view",
      "content": "Compact view shows each message on one line without names and times. Click it again for the expanded view.",
      "screenshot": "04-compact-view.png"
    },
    {
      "title": "Colours",
      "content": "The gear button opens the display settings: <strong>Display background</strong>, <strong>Source text</strong> and <strong>Translation text</strong>. When your target language has readings, the same panel has a switch for them: see <a href=\"/docs/features/pronunciation-readings\">Pronunciation Readings</a>.",
      "screenshot": "05-display-settings.png"
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "The subtitle window has its own display settings, independent of these.",
      "Clear conversation removes everything shown. Export first if you want to keep it."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "Do these settings change what gets exported?",
        "answer": "The export menu starts from what the conversation shows, but you can change its choices before exporting."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "显示设置：对话中显示哪些内容",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "对话上方的工具栏控制你看到的内容：每一方显示原文、译文还是两者，以及字号、紧凑视图和颜色。"
  },
  "steps": [
    {
      "title": "工具栏",
      "content": "从左到右依次是：<strong>我</strong>和<strong>对方</strong>各显示哪些内容、缩小和放大文字、紧凑视图、导出、显示设置和清除对话。",
      "screenshot": "01-toolbar.png"
    },
    {
      "title": "谁在说话",
      "content": "你的发言标记为<strong>我</strong>，对方的发言标记为<strong>对方</strong>。使用 Soniox（自己的密钥，或 KizunaAI 的 Soniox 引擎）时，一旦在对方那边听到两个或更多人，每个人都会有一个带编号的头像和名称：<strong>说话人 1</strong>、<strong>说话人 2</strong>，依此类推。面对面对话时，每条译文还会显示 <strong>L</strong> 或 <strong>R</strong>，表示它在哪只耳朵播放；没有读给任何人听的译文则显示一个静音图标。"
    },
    {
      "title": "原文、译文或两者",
      "content": "点击“我”或“对方”按钮，可在<strong>原文</strong>（只显示原文）、<strong>译文</strong>（只显示译文）、<strong>两者</strong>和<strong>关闭</strong>（隐藏这一方）之间循环切换。此处“我”只显示译文。",
      "screenshot": "02-me-translation-only.png"
    },
    {
      "title": "字号",
      "content": "使用缩小和放大文字按钮调整字号，范围为 12 到 64 像素。",
      "screenshot": "03-larger-text.png"
    },
    {
      "title": "紧凑视图",
      "content": "紧凑视图会把每条消息显示为一行，不显示名称和时间。再次点击即可切换回展开视图。",
      "screenshot": "04-compact-view.png"
    },
    {
      "title": "颜色",
      "content": "齿轮按钮会打开显示设置：<strong>背景颜色</strong>、<strong>原文颜色</strong>和<strong>译文颜色</strong>。如果目标语言有读音，同一个面板里还有读音开关，详见<a href=\"/docs/features/pronunciation-readings\">读音标注</a>。",
      "screenshot": "05-display-settings.png"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "字幕窗口有自己的显示设置，与这些设置互不影响。",
      "清除对话会删除显示的所有内容。如果想保留，请先导出。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "这些设置会改变导出的内容吗？",
        "answer": "导出菜单的初始选项与对话当前显示的内容一致，但你可以在导出前更改这些选项。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "表示設定：会話に表示する内容",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "会話の上にあるツールバーで表示内容を調整できます。それぞれの側の原文・訳文・両方の表示、文字サイズ、コンパクト表示、色です。"
  },
  "steps": [
    {
      "title": "ツールバー",
      "content": "左から順に、<strong>自分</strong>と<strong>相手</strong>それぞれに表示する行、文字を小さく・大きく、コンパクト表示、エクスポート、表示設定、会話をクリアです。",
      "screenshot": "01-toolbar.png"
    },
    {
      "title": "誰が話しているか",
      "content": "あなたの発言には<strong>自分</strong>、相手の発言には<strong>相手</strong>と表示されます。Soniox（自分の API キー、または KizunaAI の Soniox エンジン）では、相手側で 2 人以上の声が聞こえると、それぞれに番号付きのアバターと名前（<strong>話者 1</strong>、<strong>話者 2</strong>など）が付きます。対面での会話では、各翻訳に、再生された耳を示す <strong>L</strong> または <strong>R</strong> も表示されます。誰にも読み上げられなかった翻訳には、ミュートのスピーカーアイコンが表示されます。"
    },
    {
      "title": "原文・訳文・両方",
      "content": "自分または相手のボタンをクリックすると、<strong>原文</strong>（原文のみ）、<strong>訳文</strong>（翻訳のみ）、<strong>両方</strong>、<strong>オフ</strong>（その側を非表示）が順に切り替わります。ここでは、自分は訳文のみを表示しています。",
      "screenshot": "02-me-translation-only.png"
    },
    {
      "title": "文字サイズ",
      "content": "文字を小さく・大きくするボタンで、12〜64 ピクセルの範囲でサイズを変更できます。",
      "screenshot": "03-larger-text.png"
    },
    {
      "title": "コンパクト表示",
      "content": "コンパクト表示では、名前と時刻を省いて各メッセージを 1 行で表示します。もう一度クリックすると展開表示に戻ります。",
      "screenshot": "04-compact-view.png"
    },
    {
      "title": "色",
      "content": "歯車ボタンで表示設定が開きます。<strong>背景色</strong>、<strong>原文の色</strong>、<strong>訳文の色</strong>を設定できます。ターゲット言語に読みがある場合は、同じパネルに読みのスイッチも表示されます。詳しくは<a href=\"/docs/features/pronunciation-readings\">読みの表示</a>をご覧ください。",
      "screenshot": "05-display-settings.png"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "字幕ウィンドウにはこれとは別の独自の表示設定があります。",
      "会話をクリアすると、表示されている内容がすべて消えます。残したい場合は先にエクスポートしてください。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "これらの設定はエクスポートされる内容に影響しますか？",
        "answer": "エクスポートメニューは会話に表示されている内容を初期値にしますが、エクスポートする前に選択を変更できます。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "표시 설정: 대화에 표시되는 내용",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "대화 위의 도구 모음에서 보이는 내용을 조절합니다. 각 쪽의 원문, 번역 또는 둘 다, 글자 크기, 간략 보기, 색상입니다."
  },
  "steps": [
    {
      "title": "도구 모음",
      "content": "왼쪽부터 <strong>나</strong>와 <strong>상대방</strong> 각각 표시할 줄, 글자 작게·크게, 간략 보기, 내보내기, 표시 설정, 대화 지우기입니다.",
      "screenshot": "01-toolbar.png"
    },
    {
      "title": "누가 말하고 있는지",
      "content": "내 발화에는 <strong>나</strong>, 상대방의 발화에는 <strong>상대방</strong>이 표시됩니다. Soniox(내 API 키 사용 또는 KizunaAI의 Soniox 엔진)에서는 상대방 쪽에서 두 명 이상의 목소리가 들리면, 각 사람에게 번호가 붙은 아바타와 이름(<strong>화자 1</strong>, <strong>화자 2</strong> 등)이 표시됩니다. 대면 대화에서는 각 번역에 재생된 귀를 나타내는 <strong>L</strong> 또는 <strong>R</strong>도 표시되며, 아무에게도 읽어 주지 않은 번역에는 음소거된 스피커 아이콘이 표시됩니다."
    },
    {
      "title": "원문, 번역 또는 둘 다",
      "content": "나 또는 상대방 버튼을 클릭하면 <strong>원문</strong>(원문만), <strong>번역</strong>(번역만), <strong>모두</strong>, <strong>끄기</strong>(그쪽 숨기기) 순으로 바뀝니다. 여기서는 나에 번역만 표시하고 있습니다.",
      "screenshot": "02-me-translation-only.png"
    },
    {
      "title": "글자 크기",
      "content": "글자 작게·크게 버튼으로 크기를 12~64픽셀 범위에서 바꿀 수 있습니다.",
      "screenshot": "03-larger-text.png"
    },
    {
      "title": "간략 보기",
      "content": "간략 보기는 이름과 시간 없이 각 메시지를 한 줄로 표시합니다. 다시 클릭하면 확장 보기로 돌아갑니다.",
      "screenshot": "04-compact-view.png"
    },
    {
      "title": "색상",
      "content": "톱니바퀴 버튼을 누르면 표시 설정이 열립니다. <strong>배경색</strong>, <strong>원문 색상</strong>, <strong>번역 색상</strong>을 설정할 수 있습니다. 대상 언어에 읽는 법이 있으면 같은 패널에 읽는 법 스위치도 나타납니다. 자세한 내용은 <a href=\"/docs/features/pronunciation-readings\">읽는 법 표시</a>를 참고하세요.",
      "screenshot": "05-display-settings.png"
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "자막 창에는 이와 별개인 자체 표시 설정이 있습니다.",
      "대화 지우기는 표시된 모든 내용을 삭제합니다. 보관하려면 먼저 내보내세요."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "이 설정이 내보내는 내용을 바꾸나요?",
        "answer": "내보내기 메뉴는 대화에 표시된 내용을 기준으로 시작하지만, 내보내기 전에 선택을 바꿀 수 있습니다."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "Ajustes de visualización: qué muestra la conversación",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "La barra de herramientas situada sobre la conversación controla lo que ves: el original, la traducción o ambos para cada lado, el tamaño del texto, una vista compacta y los colores."
  },
  "steps": [
    {
      "title": "La barra de herramientas",
      "content": "De izquierda a derecha: qué líneas mostrar para <strong>Yo</strong> y para <strong>Otro</strong>, texto más pequeño y más grande, vista compacta, exportar, ajustes de visualización y borrar conversación.",
      "screenshot": "01-toolbar.png"
    },
    {
      "title": "Quién habla",
      "content": "Tus líneas se marcan con <strong>Yo</strong> y las de la otra parte con <strong>Otro</strong>. Con Soniox (tu propia clave, o el motor de Soniox de KizunaAI), en cuanto se oye a dos o más personas de la otra parte, cada una recibe un avatar numerado y un nombre, <strong>Hablante 1</strong>, <strong>Hablante 2</strong>, etc. En una conversación cara a cara, cada traducción muestra además <strong>L</strong> o <strong>R</strong> según el oído en el que sonó, o un altavoz silenciado si no se leyó a nadie."
    },
    {
      "title": "Original, traducción o ambos",
      "content": "Haz clic en el botón Yo u Otro para pasar por <strong>Origen</strong> (solo el original), <strong>Traducción</strong> (solo la traducción), <strong>Ambos</strong> y <strong>Apagado</strong> (ocultar ese lado). Aquí, Yo muestra solo la traducción.",
      "screenshot": "02-me-translation-only.png"
    },
    {
      "title": "Tamaño del texto",
      "content": "Usa los botones de texto más pequeño y más grande para cambiar el tamaño, de 12 a 64 píxeles.",
      "screenshot": "03-larger-text.png"
    },
    {
      "title": "Vista compacta",
      "content": "La vista compacta muestra cada mensaje en una sola línea, sin nombres ni horas. Vuelve a hacer clic para la vista expandida.",
      "screenshot": "04-compact-view.png"
    },
    {
      "title": "Colores",
      "content": "El botón del engranaje abre los ajustes de visualización: <strong>Color de fondo</strong>, <strong>Color del texto original</strong> y <strong>Color de la traducción</strong>. Cuando tu idioma de destino tiene lectura, el mismo panel incluye un interruptor para ella: consulta <a href=\"/docs/features/pronunciation-readings\">Lectura de la pronunciación</a>.",
      "screenshot": "05-display-settings.png"
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "La ventana de subtítulos tiene sus propios ajustes de visualización, independientes de estos.",
      "Borrar conversación elimina todo lo que se muestra. Exporta antes si quieres conservarlo."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "¿Estos ajustes cambian lo que se exporta?",
        "answer": "El menú de exportación parte de lo que muestra la conversación, pero puedes cambiar sus opciones antes de exportar."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "Paramètres d'affichage : ce que montre la conversation",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "La barre d’outils au-dessus de la conversation contrôle ce que vous voyez : l’original, la traduction ou les deux pour chaque côté, la taille du texte, une vue compacte et les couleurs."
  },
  "steps": [
    {
      "title": "La barre d’outils",
      "content": "De gauche à droite : les lignes à afficher pour <strong>Moi</strong> et pour <strong>Autre</strong>, texte plus petit et plus grand, vue compacte, export, paramètres d’affichage et effacement de la conversation.",
      "screenshot": "01-toolbar.png"
    },
    {
      "title": "Qui parle",
      "content": "Vos lignes sont marquées <strong>Moi</strong> et celles de votre interlocuteur <strong>Autre</strong>. Avec Soniox (votre propre clé, ou le moteur Soniox de KizunaAI), dès que deux personnes ou plus sont entendues de l’autre côté, chacune reçoit un avatar numéroté et un nom, <strong>Intervenant 1</strong>, <strong>Intervenant 2</strong>, etc. Dans une conversation en face à face, chaque traduction affiche aussi <strong>L</strong> (gauche) ou <strong>R</strong> (droite) selon l’oreille où elle a été lue, ou un haut-parleur barré quand elle n’a été lue à personne."
    },
    {
      "title": "Original, traduction ou les deux",
      "content": "Cliquez sur le bouton Moi ou Autre pour passer successivement par <strong>Source</strong> (l’original seul), <strong>Traduction</strong> (la traduction seule), <strong>Les deux</strong> et <strong>Arrêt</strong> (masquer ce côté). Ici, Moi n’affiche que la traduction.",
      "screenshot": "02-me-translation-only.png"
    },
    {
      "title": "Taille du texte",
      "content": "Utilisez les boutons de texte plus petit et plus grand pour changer la taille, de 12 à 64 pixels.",
      "screenshot": "03-larger-text.png"
    },
    {
      "title": "Vue compacte",
      "content": "La vue compacte affiche chaque message sur une ligne, sans noms ni heures. Cliquez à nouveau pour revenir à la vue développée.",
      "screenshot": "04-compact-view.png"
    },
    {
      "title": "Couleurs",
      "content": "Le bouton en forme d’engrenage ouvre les paramètres d’affichage : <strong>Couleur du fond</strong>, <strong>Couleur du texte source</strong> et <strong>Couleur de la traduction</strong>. Quand votre langue cible a des lectures, le même panneau contient un interrupteur pour elles : voir <a href=\"/docs/features/pronunciation-readings\">Lectures de prononciation</a>.",
      "screenshot": "05-display-settings.png"
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "La fenêtre de sous-titres a ses propres paramètres d’affichage, indépendants de ceux-ci.",
      "Effacer la conversation supprime tout ce qui est affiché. Exportez d’abord si vous voulez le garder."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "Ces paramètres modifient-ils ce qui est exporté ?",
        "answer": "Le menu d’export part de ce que la conversation affiche, mais vous pouvez modifier ses choix avant d’exporter."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "إعدادات العرض: ما تعرضه المحادثة",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "يتحكم شريط الأدوات فوق المحادثة فيما تراه: الأصل أو الترجمة أو كلاهما لكل جانب، وحجم النص، والعرض المضغوط، والألوان."
  },
  "steps": [
    {
      "title": "شريط الأدوات",
      "content": "من اليسار إلى اليمين: الأسطر المعروضة لـ<strong>أنا</strong> ولـ<strong>الآخر</strong>، وتصغير النص وتكبيره، والعرض المضغوط، والتصدير، وإعدادات العرض، ومسح المحادثة.",
      "screenshot": "01-toolbar.png"
    },
    {
      "title": "من يتحدث",
      "content": "تُعلَّم جملك بـ<strong>أنا</strong> وجمل الطرف الآخر بـ<strong>الآخر</strong>. ومع Soniox (بمفتاحك الخاص، أو محرك Soniox في KizunaAI)، ما إن يُسمع شخصان أو أكثر في الطرف الآخر حتى يحصل كل منهم على صورة رمزية مرقّمة واسم، <strong>المتحدث 1</strong> و<strong>المتحدث 2</strong> وهكذا. وفي المحادثة وجهًا لوجه، تُظهر كل ترجمة أيضًا <strong>L</strong> أو <strong>R</strong> للأذن التي شُغّلت فيها، أو رمز مكبّر صوت مكتوم إذا لم تُقرأ لأحد."
    },
    {
      "title": "الأصل أو الترجمة أو كلاهما",
      "content": "انقر على زر «أنا» أو «الآخر» للتنقل بين <strong>الأصل</strong> (الأصل فقط)، و<strong>الترجمة</strong> (الترجمة فقط)، و<strong>الكل</strong>، و<strong>إيقاف</strong> (إخفاء ذلك الجانب). هنا يعرض «أنا» الترجمة فقط.",
      "screenshot": "02-me-translation-only.png"
    },
    {
      "title": "حجم النص",
      "content": "استخدم زري تصغير النص وتكبيره لتغيير الحجم، من 12 إلى 64 بكسل.",
      "screenshot": "03-larger-text.png"
    },
    {
      "title": "العرض المضغوط",
      "content": "يعرض العرض المضغوط كل رسالة في سطر واحد بلا أسماء ولا أوقات. انقر عليه مرة أخرى للعودة إلى العرض الموسع.",
      "screenshot": "04-compact-view.png"
    },
    {
      "title": "الألوان",
      "content": "يفتح زر الترس إعدادات العرض: <strong>لون الخلفية</strong> و<strong>لون النص الأصلي</strong> و<strong>لون الترجمة</strong>. عندما تكون للغة الهدف قراءات، تحتوي اللوحة نفسها على مفتاح لها: انظر <a href=\"/docs/features/pronunciation-readings\">قراءات النطق</a>.",
      "screenshot": "05-display-settings.png"
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "لنافذة الترجمة النصية إعدادات عرض خاصة بها، مستقلة عن هذه.",
      "يزيل «مسح المحادثة» كل ما هو معروض. صدّر المحادثة أولًا إذا أردت الاحتفاظ بها."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "هل تغيّر هذه الإعدادات ما يُصدَّر؟",
        "answer": "تبدأ قائمة التصدير بما تعرضه المحادثة، لكن يمكنك تغيير خياراتها قبل التصدير."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "Configurações de exibição: o que a conversa mostra",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "A barra de ferramentas acima da conversa controla o que você vê: o original, a tradução ou ambos para cada lado, o tamanho do texto, uma visualização compacta e as cores."
  },
  "steps": [
    {
      "title": "A barra de ferramentas",
      "content": "Da esquerda para a direita: quais falas mostrar para <strong>Eu</strong> e para <strong>Outro</strong>, texto menor e maior, visualização compacta, exportar, configurações de exibição e limpar conversa.",
      "screenshot": "01-toolbar.png"
    },
    {
      "title": "Quem está falando",
      "content": "Suas falas aparecem marcadas como <strong>Eu</strong>, e as da outra pessoa como <strong>Outro</strong>. Com a Soniox (sua própria chave, ou o mecanismo Soniox da KizunaAI), assim que duas ou mais pessoas são ouvidas do outro lado, cada uma recebe um avatar numerado e um nome, <strong>Falante 1</strong>, <strong>Falante 2</strong> e assim por diante. Em uma conversa cara a cara, cada tradução também mostra <strong>L</strong> ou <strong>R</strong> conforme o ouvido em que tocou, ou um alto-falante silenciado quando não foi lida para ninguém."
    },
    {
      "title": "Original, tradução ou ambos",
      "content": "Clique no botão Eu ou Outro para alternar entre <strong>Original</strong> (só o original), <strong>Tradução</strong> (só a tradução), <strong>Ambos</strong> e <strong>Desligado</strong> (ocultar esse lado). Aqui, Eu mostra só a tradução.",
      "screenshot": "02-me-translation-only.png"
    },
    {
      "title": "Tamanho do texto",
      "content": "Use os botões de texto menor e maior para mudar o tamanho, de 12 a 64 pixels.",
      "screenshot": "03-larger-text.png"
    },
    {
      "title": "Visualização compacta",
      "content": "A visualização compacta mostra cada mensagem em uma linha, sem nomes nem horários. Clique de novo para voltar à visualização expandida.",
      "screenshot": "04-compact-view.png"
    },
    {
      "title": "Cores",
      "content": "O botão de engrenagem abre as configurações de exibição: <strong>Cor do fundo</strong>, <strong>Cor do texto original</strong> e <strong>Cor da tradução</strong>. Quando o seu idioma de destino tem leituras, o mesmo painel tem uma chave para elas: veja <a href=\"/docs/features/pronunciation-readings\">Leituras de pronúncia</a>.",
      "screenshot": "05-display-settings.png"
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "A janela de legendas tem suas próprias configurações de exibição, independentes destas.",
      "Limpar conversa remove tudo o que está sendo mostrado. Exporte antes se quiser guardar."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "Essas configurações mudam o que é exportado?",
        "answer": "O menu de exportação parte do que a conversa mostra, mas você pode mudar as opções dele antes de exportar."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Настройки отображения: что показывает разговор",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "Панель инструментов над разговором управляет тем, что вы видите: оригинал, перевод или оба для каждой стороны, размер текста, компактный вид и цвета."
  },
  "steps": [
    {
      "title": "Панель инструментов",
      "content": "Слева направо: какие строки показывать для <strong>Я</strong> и для <strong>Собеседник</strong>, уменьшение и увеличение текста, компактный вид, экспорт, настройки отображения и очистка разговора.",
      "screenshot": "01-toolbar.png"
    },
    {
      "title": "Кто говорит",
      "content": "Ваши строки отмечены меткой <strong>Я</strong>, строки собеседника — меткой <strong>Собеседник</strong>. С Soniox (ваш собственный ключ или движок Soniox в KizunaAI), как только на стороне собеседника слышно двух или более человек, каждый получает пронумерованный аватар и имя — <strong>Говорящий 1</strong>, <strong>Говорящий 2</strong> и так далее. При разговоре лицом к лицу у каждого перевода также отображается <strong>L</strong> или <strong>R</strong> — ухо, в котором он прозвучал, — или значок выключенного динамика, если его никому не зачитали."
    },
    {
      "title": "Оригинал, перевод или оба",
      "content": "Нажимайте кнопку «Я» или «Собеседник», чтобы переключаться между <strong>Оригинал</strong> (только оригинал), <strong>Перевод</strong> (только перевод), <strong>Оба</strong> и <strong>Отключено</strong> (скрыть эту сторону). Здесь для «Я» показан только перевод.",
      "screenshot": "02-me-translation-only.png"
    },
    {
      "title": "Размер текста",
      "content": "Кнопками уменьшения и увеличения текста меняйте размер от 12 до 64 пикселей.",
      "screenshot": "03-larger-text.png"
    },
    {
      "title": "Компактный вид",
      "content": "Компактный вид показывает каждое сообщение в одну строку без имён и времени. Нажмите ещё раз, чтобы вернуться к развёрнутому виду.",
      "screenshot": "04-compact-view.png"
    },
    {
      "title": "Цвета",
      "content": "Кнопка с шестерёнкой открывает настройки отображения: <strong>Цвет фона</strong>, <strong>Цвет исходного текста</strong> и <strong>Цвет перевода</strong>. Когда у целевого языка есть чтения, на той же панели есть переключатель для них: см. <a href=\"/docs/features/pronunciation-readings\">Чтения (произношение)</a>.",
      "screenshot": "05-display-settings.png"
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "У окна субтитров свои настройки отображения, независимые от этих.",
      "«Очистить разговор» удаляет всё показанное. Если хотите сохранить, сначала сделайте экспорт."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Влияют ли эти настройки на экспорт?",
        "answer": "Меню экспорта исходно следует тому, что показывает разговор, но перед экспортом выбор можно изменить."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Anzeigeeinstellungen: was die Unterhaltung zeigt",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "Die Symbolleiste über der Unterhaltung steuert, was Sie sehen: Original, Übersetzung oder beides für jede Seite, die Textgröße, eine kompakte Ansicht und die Farben."
  },
  "steps": [
    {
      "title": "Die Symbolleiste",
      "content": "Von links nach rechts: welche Zeilen für <strong>Ich</strong> und für <strong>Gegenüber</strong> angezeigt werden, Text verkleinern und vergrößern, kompakte Ansicht, Export, Anzeigeeinstellungen und Unterhaltung löschen.",
      "screenshot": "01-toolbar.png"
    },
    {
      "title": "Wer spricht",
      "content": "Ihre Zeilen sind mit <strong>Ich</strong> markiert, die Ihres Gegenübers mit <strong>Gegenüber</strong>. Mit Soniox (eigener Schlüssel oder die Soniox-Engine von KizunaAI) erhält, sobald auf der anderen Seite zwei oder mehr Personen zu hören sind, jede von ihnen einen nummerierten Avatar und einen Namen, <strong>Sprecher 1</strong>, <strong>Sprecher 2</strong> und so weiter. In einem Gespräch von Angesicht zu Angesicht zeigt jede Übersetzung außerdem <strong>L</strong> oder <strong>R</strong> für das Ohr, in dem sie abgespielt wurde, oder einen stummgeschalteten Lautsprecher, wenn sie niemandem vorgelesen wurde."
    },
    {
      "title": "Original, Übersetzung oder beides",
      "content": "Klicken Sie auf die Schaltfläche „Ich“ oder „Gegenüber“, um zwischen <strong>Original</strong> (nur das Original), <strong>Übersetzung</strong> (nur die Übersetzung), <strong>Beides</strong> und <strong>Aus</strong> (diese Seite ausblenden) zu wechseln. Hier zeigt „Ich“ nur die Übersetzung.",
      "screenshot": "02-me-translation-only.png"
    },
    {
      "title": "Textgröße",
      "content": "Mit den Schaltflächen zum Verkleinern und Vergrößern ändern Sie die Größe von 12 bis 64 Pixel.",
      "screenshot": "03-larger-text.png"
    },
    {
      "title": "Kompakte Ansicht",
      "content": "Die kompakte Ansicht zeigt jede Nachricht in einer Zeile ohne Namen und Uhrzeiten. Klicken Sie erneut für die erweiterte Ansicht.",
      "screenshot": "04-compact-view.png"
    },
    {
      "title": "Farben",
      "content": "Die Zahnrad-Schaltfläche öffnet die Anzeigeeinstellungen: <strong>Hintergrundfarbe</strong>, <strong>Quelltextfarbe</strong> und <strong>Übersetzungsfarbe</strong>. Hat Ihre Zielsprache Lesungen, enthält dasselbe Fenster einen Schalter dafür: siehe <a href=\"/docs/features/pronunciation-readings\">Lesungen (Aussprache)</a>.",
      "screenshot": "05-display-settings.png"
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "Das Untertitelfenster hat eigene Anzeigeeinstellungen, unabhängig von diesen.",
      "„Unterhaltung löschen“ entfernt alles, was angezeigt wird. Exportieren Sie vorher, wenn Sie es behalten möchten."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Ändern diese Einstellungen, was exportiert wird?",
        "answer": "Das Exportmenü beginnt mit dem, was die Unterhaltung anzeigt, aber Sie können die Auswahl vor dem Export ändern."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Налаштування відображення: що показує розмова",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "Панель інструментів над розмовою керує тим, що ви бачите: оригінал, переклад або обидва для кожної сторони, розмір тексту, компактний вигляд і кольори."
  },
  "steps": [
    {
      "title": "Панель інструментів",
      "content": "Зліва направо: які рядки показувати для <strong>Я</strong> і для <strong>Співрозмовник</strong>, зменшення та збільшення тексту, компактний вигляд, експорт, налаштування відображення й очищення розмови.",
      "screenshot": "01-toolbar.png"
    },
    {
      "title": "Хто говорить",
      "content": "Ваші рядки позначено міткою <strong>Я</strong>, рядки співрозмовника — міткою <strong>Співрозмовник</strong>. З Soniox (ваш власний ключ або рушій Soniox у KizunaAI), щойно на боці співрозмовника чути двох або більше людей, кожен отримує пронумерований аватар та ім’я — <strong>Мовець 1</strong>, <strong>Мовець 2</strong> і так далі. Під час розмови віч-на-віч біля кожного перекладу також показано <strong>L</strong> або <strong>R</strong> — вухо, у якому він прозвучав, — або значок вимкненого динаміка, якщо його нікому не зачитали."
    },
    {
      "title": "Оригінал, переклад або обидва",
      "content": "Натискайте кнопку «Я» або «Співрозмовник», щоб перемикатися між <strong>Оригінал</strong> (лише оригінал), <strong>Переклад</strong> (лише переклад), <strong>Обидва</strong> та <strong>Вимкнено</strong> (приховати цю сторону). Тут для «Я» показано лише переклад.",
      "screenshot": "02-me-translation-only.png"
    },
    {
      "title": "Розмір тексту",
      "content": "Кнопками зменшення та збільшення тексту змінюйте розмір від 12 до 64 пікселів.",
      "screenshot": "03-larger-text.png"
    },
    {
      "title": "Компактний вигляд",
      "content": "Компактний вигляд показує кожне повідомлення в один рядок без імен і часу. Натисніть ще раз, щоб повернутися до розгорнутого вигляду.",
      "screenshot": "04-compact-view.png"
    },
    {
      "title": "Кольори",
      "content": "Кнопка з шестернею відкриває налаштування відображення: <strong>Колір тла</strong>, <strong>Колір вихідного тексту</strong> і <strong>Колір перекладу</strong>. Коли в цільової мови є читання, на тій самій панелі є перемикач для них: див. <a href=\"/docs/features/pronunciation-readings\">Читання (вимова)</a>.",
      "screenshot": "05-display-settings.png"
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "Вікно субтитрів має власні налаштування відображення, незалежні від цих.",
      "«Очистити розмову» видаляє все показане. Якщо хочете зберегти, спочатку зробіть експорт."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Чи впливають ці налаштування на експорт?",
        "answer": "Меню експорту спочатку відповідає тому, що показує розмова, але перед експортом вибір можна змінити."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "Impostazioni di visualizzazione: cosa mostra la conversazione",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "La barra degli strumenti sopra la conversazione controlla ciò che vedi: l’originale, la traduzione o entrambi per ciascuna parte, la dimensione del testo, una vista compatta e i colori."
  },
  "steps": [
    {
      "title": "La barra degli strumenti",
      "content": "Da sinistra a destra: quali righe mostrare per <strong>Io</strong> e per <strong>Altro</strong>, testo più piccolo e più grande, vista compatta, esportazione, impostazioni di visualizzazione e cancellazione della conversazione.",
      "screenshot": "01-toolbar.png"
    },
    {
      "title": "Chi parla",
      "content": "Le tue righe sono contrassegnate da <strong>Io</strong> e quelle dell’altra parte da <strong>Altro</strong>. Con Soniox (la tua chiave, o il motore Soniox di KizunaAI), quando dall’altra parte si sentono due o più persone, ognuna riceve un avatar numerato e un nome, <strong>Interlocutore 1</strong>, <strong>Interlocutore 2</strong> e così via. In una conversazione faccia a faccia, ogni traduzione mostra anche <strong>L</strong> (sinistra) o <strong>R</strong> (destra), l’orecchio in cui è stata riprodotta, oppure un altoparlante barrato quando non è stata letta a nessuno."
    },
    {
      "title": "Originale, traduzione o entrambi",
      "content": "Fai clic sul pulsante Io o Altro per passare tra <strong>Originale</strong> (solo l’originale), <strong>Traduzione</strong> (solo la traduzione), <strong>Entrambi</strong> e <strong>Spento</strong> (nasconde quella parte). Qui Io mostra solo la traduzione.",
      "screenshot": "02-me-translation-only.png"
    },
    {
      "title": "Dimensione del testo",
      "content": "Usa i pulsanti per rimpicciolire e ingrandire il testo, da 12 a 64 pixel.",
      "screenshot": "03-larger-text.png"
    },
    {
      "title": "Vista compatta",
      "content": "La vista compatta mostra ogni messaggio su una riga, senza nomi né orari. Fai di nuovo clic per tornare alla vista espansa.",
      "screenshot": "04-compact-view.png"
    },
    {
      "title": "Colori",
      "content": "Il pulsante a forma di ingranaggio apre le impostazioni di visualizzazione: <strong>Colore dello sfondo</strong>, <strong>Colore testo originale</strong> e <strong>Colore traduzione</strong>. Quando la lingua di destinazione ha le letture, lo stesso pannello ha un interruttore per attivarle: vedi <a href=\"/docs/features/pronunciation-readings\">Letture di pronuncia</a>.",
      "screenshot": "05-display-settings.png"
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "La finestra dei sottotitoli ha le sue impostazioni di visualizzazione, indipendenti da queste.",
      "Cancella conversazione rimuove tutto ciò che è mostrato. Esporta prima, se vuoi conservarlo."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "Queste impostazioni cambiano ciò che viene esportato?",
        "answer": "Il menu di esportazione parte da ciò che la conversazione mostra, ma puoi modificarne le scelte prima di esportare."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function FeatureDisplay() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/display" />;
}
